import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AssetIcon,
  AssetPreview,
  Button,
  Card,
  Cluster,
  ConnectorIcon,
  EmptyState,
  Field,
  Grid,
  Input,
  Inspector,
  LayersIcon,
  Modal,
  Page,
  QueryState,
  Select,
  SparkleIcon,
  Stack,
  StatStrip,
  type InspectorField,
} from '@integra/ui';
import {
  useAssetPreview,
  useAssets,
  useCreateAsset,
  useDeleteSavedView,
  useSaveView,
  useSavedViews,
} from '../api/hooks.js';
import { CanvaImportModal } from './CanvaImportModal.js';
import { GenerateImageModal } from './GenerateImageModal.js';
import type { Asset, SavedView } from '../api/types.js';

/**
 * Asset catalogue for a workspace (§6.1). Assets are the unit INTEGRA
 * distributes, so this is the working surface an operator returns to.
 *
 * All data access goes through the hooks layer (§21.4) — no fetch here.
 */

const ASSET_TYPES = [
  'product',
  'article',
  'image',
  'brand_config',
  'template',
  'campaign_brief',
] as const;

const ASSET_STATES = ['draft', 'active', 'delisted', 'archived'] as const;

const VISIBILITIES = ['private', 'restricted', 'public'] as const;

/**
 * Asset `data` is an open bag (§6.1), so nothing about it can be assumed. Read
 * a field only when it is genuinely the type we need, and fall back honestly
 * rather than coercing whatever happens to be there into a label.
 */
/**
 * One asset tile.
 *
 * A rendered creative stores its bytes behind an authenticated endpoint, not at
 * a public URL, so the thumbnail has to be fetched through the API client and
 * wrapped in a Blob URL (§21.4). That needs a hook, and a hook needs its own
 * component — hence a card rather than an inline map.
 *
 * Assets with no stored media skip the request entirely rather than firing one
 * per tile and having the endpoint reject it.
 */
function AssetCard({
  workspaceId,
  asset,
  onInspect,
}: {
  workspaceId: string;
  asset: Asset;
  onInspect: () => void;
}) {
  const hasStoredMedia = readString(asset.data, 'mediaPath') !== null;
  const preview = useAssetPreview(workspaceId, hasStoredMedia ? asset.id : '');
  // An externally hosted image (an ingested product photo) is already a URL.
  const externalUrl = readString(asset.data, 'imageUrl');
  // For a text asset with no creative, the words ARE the preview.
  const body =
    readString(asset.data, 'body') ??
    readString(asset.data, 'text') ??
    readString(asset.data, 'caption') ??
    readString(asset.data, 'description');

  return (
    <AssetPreview
      title={readString(asset.data, 'title') ?? asset.id}
      mediaUrl={preview.url ?? externalUrl}
      body={body}
      type={asset.type}
      state={asset.state}
      footer={
        <Cluster gap="md">
          <Link
            to={`/w/${workspaceId}/assets/${asset.id}`}
            className="text-sm font-semibold text-navy-700 hover:underline"
          >
            Open asset
          </Link>
          <button
            type="button"
            onClick={onInspect}
            className="text-sm text-ink-muted hover:text-navy-700 hover:underline"
          >
            Inspect
          </button>
        </Cluster>
      }
    />
  );
}

/** Provenance + raw detail for any asset — the same drawer every object opens. */
function assetInspectorFields(asset: Asset): InspectorField[] {
  const fmt = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };
  return [
    { label: 'Type', value: asset.type },
    { label: 'State', value: asset.state },
    { label: 'Visibility', value: asset.visibility },
    { label: 'Version', value: `v${String(asset.version)}` },
    {
      label: 'Origin',
      value: asset.lineage.sourceId === null ? 'Created in INTEGRA' : 'Synced from a source',
    },
    ...(asset.lineage.externalId != null
      ? [{ label: 'External id', value: asset.lineage.externalId }]
      : []),
    { label: 'Created', value: fmt(asset.createdAt) },
    { label: 'Updated', value: fmt(asset.updatedAt) },
    { label: 'Asset id', value: <span className="font-mono text-xs">{asset.id}</span> },
  ];
}

function readString(data: Record<string, unknown>, key: string): string | null {
  const value = data[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function AssetsPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();

  const [typeFilter, setTypeFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  // The default view hides archived assets — archived is terminal (§6.2), so it
  // is dead weight in a working catalogue. `''` = default (exclude archived),
  // `'all'` = opt archived back in, any specific state = exactly that state
  // (so choosing "archived" still shows the archive). exactOptionalPropertyTypes:
  // an unset filter must be ABSENT, not `undefined`.
  const stateParams =
    stateFilter === ''
      ? { includeArchived: false }
      : stateFilter === 'all'
        ? { includeArchived: true }
        : { state: stateFilter };
  const { data, isLoading, error } = useAssets(workspaceId, {
    ...(typeFilter === '' ? {} : { type: typeFilter }),
    ...stateParams,
  });

  const createAsset = useCreateAsset(workspaceId);

  // Saved views (§17.2): a user's named filter snapshots for this surface.
  const savedViews = useSavedViews(workspaceId, 'assets');
  const saveView = useSaveView(workspaceId);
  const deleteView = useDeleteSavedView(workspaceId);
  const views = savedViews.data?.data ?? [];
  const [selectedViewId, setSelectedViewId] = useState('');
  const [viewName, setViewName] = useState('');

  const applyView = (view: SavedView): void => {
    const cfg = view.config;
    setTypeFilter(typeof cfg['typeFilter'] === 'string' ? cfg['typeFilter'] : '');
    setStateFilter(typeof cfg['stateFilter'] === 'string' ? cfg['stateFilter'] : '');
  };

  const saveCurrentView = (): void => {
    const name = viewName.trim();
    if (name === '') return;
    saveView.mutate(
      { surface: 'assets', name, config: { typeFilter, stateFilter } },
      { onSuccess: () => setViewName('') },
    );
  };

  const [open, setOpen] = useState(false);
  const [canvaOpen, setCanvaOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [inspecting, setInspecting] = useState<Asset | null>(null);
  const [newType, setNewType] = useState<string>('product');
  const [newTitle, setNewTitle] = useState('');
  const [newVisibility, setNewVisibility] = useState<string>('private');

  const submit = (): void => {
    createAsset.mutate(
      { type: newType, data: { title: newTitle }, visibility: newVisibility },
      {
        onSuccess: () => {
          setOpen(false);
          setNewTitle('');
        },
      },
    );
  };

  const assets = data?.data ?? [];
  const filtered = typeFilter !== '' || stateFilter !== '';

  return (
    <Page
      title="Assets"
      icon={AssetIcon}
      description="Everything this workspace can distribute: products, articles, images and the briefs that drive them."
      stats={
        assets.length > 0 ? (
          <StatStrip
            stats={[
              { icon: AssetIcon, label: filtered ? 'Matching' : 'Assets', value: assets.length },
            ]}
          />
        ) : undefined
      }
      actions={
        <Cluster gap="sm">
          <Button variant="secondary" onClick={() => setGenerateOpen(true)}>
            Generate with AI
          </Button>
          <Button variant="secondary" onClick={() => setCanvaOpen(true)}>
            Import from Canva
          </Button>
          <Button onClick={() => setOpen(true)}>New asset</Button>
        </Cluster>
      }
    >
      <Stack gap="lg">
        <Card title="Filter">
          <Cluster gap="md" align="end">
            <Field label="Type" htmlFor="asset-filter-type">
              <Select
                id="asset-filter-type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">All types</option>
                {ASSET_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="State" htmlFor="asset-filter-state">
              <Select
                id="asset-filter-state"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              >
                <option value="">All (excludes archived)</option>
                {ASSET_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
                <option value="all">All incl. archived</option>
              </Select>
            </Field>
            <Button
              variant="ghost"
              onClick={() => {
                setTypeFilter('');
                setStateFilter('');
              }}
            >
              Clear
            </Button>
          </Cluster>

          {/* Saved views — apply a named filter snapshot, or save the current one. */}
          <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-edge pt-3">
            <Field label="Saved views" htmlFor="asset-view-select">
              <Select
                id="asset-view-select"
                value={selectedViewId}
                onChange={(e) => {
                  setSelectedViewId(e.target.value);
                  const view = views.find((x) => x.id === e.target.value);
                  if (view !== undefined) applyView(view);
                }}
              >
                <option value="">
                  {views.length === 0 ? 'No saved views yet' : 'Choose a view…'}
                </option>
                {views.map((view) => (
                  <option key={view.id} value={view.id}>
                    {view.name}
                  </option>
                ))}
              </Select>
            </Field>
            {selectedViewId !== '' && (
              <Button
                variant="ghost"
                loading={deleteView.isPending}
                onClick={() =>
                  deleteView.mutate(selectedViewId, { onSuccess: () => setSelectedViewId('') })
                }
              >
                Delete
              </Button>
            )}
            <div className="ml-auto flex items-end gap-2">
              <Field label="Save current as" htmlFor="asset-view-name">
                <Input
                  id="asset-view-name"
                  value={viewName}
                  placeholder="e.g. Draft products"
                  onChange={(e) => setViewName(e.target.value)}
                />
              </Field>
              <Button
                variant="secondary"
                disabled={viewName.trim() === ''}
                loading={saveView.isPending}
                onClick={saveCurrentView}
              >
                Save view
              </Button>
            </div>
          </div>
        </Card>

        <QueryState loading={isLoading} error={error} what="assets">
          {data !== undefined && assets.length === 0 ? (
            <EmptyState
              icon={AssetIcon}
              title={filtered ? 'No assets match this filter' : 'No assets yet'}
              features={[
                { icon: LayersIcon, label: 'Products, articles, images & briefs' },
                { icon: SparkleIcon, label: 'Generate with AI or import from Canva' },
                { icon: ConnectorIcon, label: 'Sync a catalogue from a source' },
              ]}
              // An empty filtered result is not an empty catalogue; saying so
              // stops an operator concluding their data vanished.
              description={
                filtered
                  ? 'The workspace may still hold assets of other types or states. Clear the filter to see them.'
                  : 'Create one by hand, or connect a source and sync a catalogue in.'
              }
              action={
                filtered ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setTypeFilter('');
                      setStateFilter('');
                    }}
                  >
                    Clear filter
                  </Button>
                ) : (
                  <Button onClick={() => setOpen(true)}>Create an asset</Button>
                )
              }
            />
          ) : (
            <Grid min="sm">
              {assets.map((asset) => (
                <AssetCard
                  key={asset.id}
                  workspaceId={workspaceId}
                  asset={asset}
                  onInspect={() => setInspecting(asset)}
                />
              ))}
            </Grid>
          )}
        </QueryState>
      </Stack>

      <Inspector
        open={inspecting !== null}
        title={
          inspecting !== null ? (readString(inspecting.data, 'title') ?? inspecting.id) : 'Asset'
        }
        {...(inspecting !== null ? { subtitle: `${inspecting.type} · ${inspecting.state}` } : {})}
        onClose={() => setInspecting(null)}
        fields={inspecting !== null ? assetInspectorFields(inspecting) : []}
      >
        {inspecting !== null && (
          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-ink-muted">
              Raw data
            </p>
            <pre className="max-h-72 overflow-auto rounded-md border border-edge bg-surface-sunken p-3 font-mono text-[11px] leading-relaxed text-navy-700">
              {JSON.stringify(inspecting.data, null, 2)}
            </pre>
          </div>
        )}
      </Inspector>

      <CanvaImportModal
        workspaceId={workspaceId}
        open={canvaOpen}
        onClose={() => setCanvaOpen(false)}
      />

      <GenerateImageModal
        workspaceId={workspaceId}
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
      />

      <Modal
        open={open}
        title="New asset"
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} loading={createAsset.isPending}>
              Create
            </Button>
          </>
        }
      >
        <Stack gap="md">
          <Field label="Type" htmlFor="asset-new-type">
            <Select
              id="asset-new-type"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
            >
              {ASSET_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Title" htmlFor="asset-new-title">
            <Input
              id="asset-new-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Autumn hamper"
            />
          </Field>
          <Field
            label="Visibility"
            htmlFor="asset-new-visibility"
            hint="New assets start in draft; visibility governs who may see them once active."
            {...(createAsset.error instanceof Error ? { error: createAsset.error.message } : {})}
          >
            <Select
              id="asset-new-visibility"
              value={newVisibility}
              onChange={(e) => setNewVisibility(e.target.value)}
            >
              {VISIBILITIES.map((visibility) => (
                <option key={visibility} value={visibility}>
                  {visibility}
                </option>
              ))}
            </Select>
          </Field>
        </Stack>
      </Modal>
    </Page>
  );
}
